import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { ArrowRight, Coins, FileSearch, ListChecks, Plus, ShieldCheck, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { CheckRow } from '@/components/checks';
import { AnimatedNumber, Page, Rise } from '@/components/motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { EmptyState, ErrorState, Skeleton } from '@/components/ui/misc';
import { checksApi, coinsApi } from '@/lib/api/endpoints';
import { useMe } from '@/lib/auth';
import { formatMoney } from '@/lib/utils';
import { isInProgress, isVisaActive } from '@/lib/visa';

function greeting(): string {
  const h = new Date().getHours();
  return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
}

export function DashboardPage() {
  const me = useMe();
  const recent = useQuery({
    queryKey: ['checks', { limit: 5, offset: 0 }],
    queryFn: () => checksApi.list({ limit: 5 }),
    refetchInterval: (q) => (q.state.data?.some((c) => isInProgress(c.status)) ? 4000 : false),
  });
  const pricing = useQuery({ queryKey: ['pricing'], queryFn: coinsApi.pricing, staleTime: 60_000 });
  const cost = pricing.data?.rules.check_cost_coins ?? 1;
  const checksLeft = cost > 0 ? Math.floor(me.coin_balance / cost) : Infinity;
  const activeCount = recent.data?.filter(isVisaActive).length ?? 0;
  const running = recent.data?.filter((c) => isInProgress(c.status)).length ?? 0;

  return (
    <Page className="space-y-6">
      {/* Hero */}
      <Rise>
        <div className="relative overflow-hidden rounded-3xl bg-slate-950 p-6 text-white shadow-2xl sm:p-8">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(247,201,72,0.32),transparent_55%),radial-gradient(ellipse_at_bottom_left,rgba(51,65,85,0.7),transparent_60%)]" />
          <div className="grid-pattern absolute inset-0 opacity-30 [mask-image:linear-gradient(to_bottom,black,transparent)]" />
          <motion.div
            className="absolute -right-10 -top-10 hidden size-56 rounded-full border border-white/10 sm:block"
            animate={{ rotate: 360 }}
            transition={{ repeat: Infinity, duration: 40, ease: 'linear' }}
          >
            <span className="absolute left-1/2 top-0 size-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand-sun shadow-[0_0_20px_rgba(247,201,72,0.9)]" />
          </motion.div>
          <div className="relative flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
            <div className="space-y-2">
              <p className="text-sm font-medium text-white/60">{greeting()},</p>
              <h1 className="text-3xl font-extrabold tracking-tight sm:text-4xl">{me.full_name?.split(' ')[0] || me.username} 👋</h1>
              <p className="max-w-md text-white/65">
                Check a UAE visa against the ICP portal. If it's <span className="font-semibold text-emerald-300">ACTIVE</span>, you can download the report right away.
              </p>
            </div>
            <Button asChild size="lg" variant="brand" className="group">
              <Link to="/checks/new">
                <Plus /> New check <ArrowRight className="transition-transform group-hover:translate-x-1" />
              </Link>
            </Button>
          </div>
        </div>
      </Rise>

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-3">
        <Rise>
          <Link to="/coins/buy" className="group block h-full">
            <Card className="relative h-full overflow-hidden p-5 transition-all hover:-translate-y-0.5 hover:shadow-glow">
              <div className="absolute -right-6 -top-6 size-24 rounded-full bg-amber-400/20 blur-2xl transition-transform group-hover:scale-150" />
              <div className="relative flex items-center justify-between">
                <span className="text-sm font-medium text-muted-foreground">Coin balance</span>
                <span className="flex size-9 items-center justify-center rounded-xl bg-gradient-to-br from-amber-300 to-amber-500 text-amber-950">
                  <Coins className="size-4" />
                </span>
              </div>
              <p className="relative mt-3 text-4xl font-black tracking-tight">
                <AnimatedNumber value={me.coin_balance} />
              </p>
              <p className="relative mt-1 text-xs text-muted-foreground">
                {cost === 0 ? 'Checks are free right now' : `Enough for ${checksLeft} check${checksLeft === 1 ? '' : 's'}`}
                <span className="ml-1 font-semibold text-primary-ink opacity-0 transition-opacity group-hover:opacity-100">· Buy more →</span>
              </p>
            </Card>
          </Link>
        </Rise>
        <Rise>
          <Card className="relative h-full overflow-hidden p-5">
            <div className="absolute -right-6 -top-6 size-24 rounded-full bg-sky-400/20 blur-2xl" />
            <div className="relative flex items-center justify-between">
              <span className="text-sm font-medium text-muted-foreground">In progress</span>
              <span className="flex size-9 items-center justify-center rounded-xl bg-sky-500/15 text-sky-600 dark:text-sky-400">
                <ListChecks className="size-4" />
              </span>
            </div>
            <p className="relative mt-3 text-4xl font-black tracking-tight">
              <AnimatedNumber value={running} />
            </p>
            <p className="relative mt-1 text-xs text-muted-foreground">of your latest checks</p>
          </Card>
        </Rise>
        <Rise>
          <Card className="relative h-full overflow-hidden p-5">
            <div className="absolute -right-6 -top-6 size-24 rounded-full bg-emerald-400/20 blur-2xl" />
            <div className="relative flex items-center justify-between">
              <span className="text-sm font-medium text-muted-foreground">Active visas found</span>
              <span className="flex size-9 items-center justify-center rounded-xl bg-emerald-500/15 text-emerald-600 dark:text-emerald-400">
                <ShieldCheck className="size-4" />
              </span>
            </div>
            <p className="relative mt-3 text-4xl font-black tracking-tight">
              <AnimatedNumber value={activeCount} />
            </p>
            <p className="relative mt-1 text-xs text-muted-foreground">reports ready to download</p>
          </Card>
        </Rise>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <Rise>
          <Card>
            <CardHeader className="flex-row items-center justify-between space-y-0">
              <CardTitle>Recent checks</CardTitle>
              <Button asChild variant="ghost" size="sm">
                <Link to="/checks">
                  View all <ArrowRight />
                </Link>
              </Button>
            </CardHeader>
            <div className="divide-y border-t">
              {recent.isPending ? (
                Array.from({ length: 3 }, (_, i) => (
                  <div key={i} className="flex items-center gap-4 px-5 py-4">
                    <Skeleton className="size-10 rounded-xl" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="w-32" />
                      <Skeleton className="h-3 w-48" />
                    </div>
                  </div>
                ))
              ) : recent.isError ? (
                <ErrorState error={recent.error} onRetry={() => recent.refetch()} />
              ) : recent.data.length === 0 ? (
                <EmptyState
                  icon={<FileSearch />}
                  title="No checks yet"
                  description="Run your first visa check. It only takes the passport number, expiry date and nationality."
                  action={
                    <Button asChild variant="brand">
                      <Link to="/checks/new">
                        <Plus /> Start a check
                      </Link>
                    </Button>
                  }
                />
              ) : (
                recent.data.map((c) => <CheckRow key={c.id} check={c} to={`/checks/${c.id}`} />)
              )}
            </div>
          </Card>
        </Rise>

        <Rise>
          <Card className="relative overflow-hidden">
            <div className="absolute inset-x-0 top-0 h-1 bg-brand-gradient animate-gradient-pan" />
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="size-4 text-amber-500" /> How it works
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ol className="space-y-4">
                {[
                  ['Enter passport details', `Each check costs ${cost} coin${cost === 1 ? '' : 's'}.`],
                  ['We query the ICP portal', 'Usually 1–5 minutes. You can leave the page.'],
                  ['Get your result', 'If ACTIVE, download the PDF report.'],
                ].map(([t, d], i) => (
                  <li key={t} className="flex gap-3">
                    <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-foreground text-xs font-bold text-background">{i + 1}</span>
                    <span>
                      <span className="block text-sm font-semibold">{t}</span>
                      <span className="block text-xs text-muted-foreground">{d}</span>
                    </span>
                  </li>
                ))}
              </ol>
              {pricing.data && (
                <p className="mt-5 rounded-lg bg-secondary/70 px-3 py-2 text-xs text-muted-foreground">
                  Failed checks are refunded automatically. Coins cost {formatMoney(pricing.data.rules.price_per_coin, pricing.data.rules.currency)} each.
                </p>
              )}
            </CardContent>
          </Card>
        </Rise>
      </div>
    </Page>
  );
}
